/**
 * GroupProgressPage — ad-hoc view for group-level schedule KPI progress.
 *
 * Rendered inside the KPINavigator space when the user clicks a KPI shape
 * on a group row in the KPIGridTable.  Shows:
 *
 *  1. Header with back button + group label + project count.
 *  2. (Groups of groups only) Aggregation method selector.
 *  3. Horizontal bar chart — one bar per project or per subgroup.
 *  4. Scrollable project/subgroup list below with name, percentage, colour.
 *
 * Supports recursive drill-down: clicking a subgroup bar renders a new
 * GroupProgressPage for that subgroup.  The internal `drillStack` tracks
 * history so the back button can unwind.
 */

import React, { useState, useMemo, useCallback, useRef, useEffect, useId } from "react";
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from "./GroupProgressPage.module.css";
import {
  buildProjectItems,
  buildSubgroupItems,
  buildFinanceProjectItems,
  buildFinanceSubgroupItems,
  buildScoreProjectItems,
  buildScoreSubgroupItems,
  type ProgressItem,
  type AggregationMethod,
} from "./groupProgressUtils";
import { formatPercent, formatFixed, formatDelta } from '../../utils/numberFormat';
import { mediaSrc } from '../../api/mediaUrl';

// ---------------------------------------------------------------------------
// Sort options
// ---------------------------------------------------------------------------

type SortMode = "alpha" | "pctDesc" | "pctAsc" | "score" | "variance";

const SORT_OPTIONS: { key: SortMode; label: string }[] = [
  { key: "alpha", label: "A–Z" },
  { key: "pctDesc", label: "% High→Low" },
  { key: "pctAsc", label: "% Low→High" },
  { key: "score", label: "Score" },
  { key: "variance", label: "Variance" },
];

function applySortMode(items: ProgressItem[], mode: SortMode): ProgressItem[] {
  const sorted = [...items];
  switch (mode) {
    case "pctDesc":
      sorted.sort((a, b) => b.percentage - a.percentage);
      break;
    case "pctAsc":
      sorted.sort((a, b) => a.percentage - b.percentage);
      break;
    case "score":
      // Worst (red=1) first, then yellow=2, green=3, grey=0 last
      sorted.sort((a, b) => {
        const sa = a.colorCode === 0 ? 99 : a.colorCode;
        const sb = b.colorCode === 0 ? 99 : b.colorCode;
        return sa - sb || a.label.localeCompare(b.label);
      });
      break;
    case "variance":
      // Largest negative variance (behind baseline) first
      sorted.sort((a, b) => {
        const va = a.baselinePercentage !== null ? a.percentage - a.baselinePercentage : 0;
        const vb = b.baselinePercentage !== null ? b.percentage - b.baselinePercentage : 0;
        return va - vb || a.label.localeCompare(b.label);
      });
      break;
    default:
      // alpha — already sorted by label from build functions
      sorted.sort((a, b) => a.label.localeCompare(b.label));
      break;
  }
  return sorted;
}

// ---------------------------------------------------------------------------
// localStorage key shared with KpiGridTable
// ---------------------------------------------------------------------------
const AGG_METHOD_LS_KEY = "kpi_group_aggregation_method";

function readAggMethod(): AggregationMethod {
  try {
    const v = window?.localStorage?.getItem(AGG_METHOD_LS_KEY);
    return v === "worst" || v === "best" ? v : "average";
  } catch {
    return "average";
  }
}

function writeAggMethod(m: AggregationMethod) {
  try {
    window?.localStorage?.setItem(AGG_METHOD_LS_KEY, m);
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// FitText — scales font size so the text fills ~70% of parent width
// ---------------------------------------------------------------------------

const FIT_TARGET = 0.70;   // fill 70% of container width
const FIT_MIN_PX = 12;
const FIT_MAX_PX = 48;

function FitText({ text, className }: { text: string; className?: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    const span = textRef.current;
    if (!container || !span) return;

    const compute = () => {
      // Cancel any pending frame to avoid stacking
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const cw = container.offsetWidth;
        if (!cw) return;
        const targetW = cw * FIT_TARGET;

        // Binary search for optimal font size
        let lo = FIT_MIN_PX;
        let hi = Math.min(FIT_MAX_PX, cw * 0.25); // cap at 25% of width

        while (hi - lo > 0.5) {
          const mid = (lo + hi) / 2;
          span.style.fontSize = `${mid}px`;
          if (span.scrollWidth > targetW) {
            hi = mid;
          } else {
            lo = mid;
          }
        }
        // Apply final size directly to DOM (no React state → no extra render → no observer loop)
        span.style.fontSize = `${lo}px`;
      });
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [text]);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        padding: "0 8px",
      }}
    >
      <span
        ref={textRef}
        className={className}
        style={{
          fontSize: FIT_MAX_PX,
          fontWeight: 700,
          whiteSpace: "nowrap",
          textAlign: "center",
        }}
      >
        {text}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  /** The __payload from the group row that was clicked. */
  groupPayload: any;
  /** Called when the user presses the top-level back button (exits group view). */
  onBack: () => void;
  /** Called when the user clicks a leaf-project bar or list row. */
  onSelectProject: (item: any, tabIndex: number) => void;
  /** Label of the KPI category being compared (e.g. "Schedule", "Budget"). */
  kpiCategoryLabel?: string;
  /** Which data source to read from. Defaults to "schedule". */
  kpiCategory?: "schedule" | "finance" | "summary" | "mission" | "data";
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const GroupProgressPage: React.FC<Props> = ({
  groupPayload,
  onBack,
  onSelectProject,
  kpiCategoryLabel = "Schedule",
  kpiCategory = "schedule",
}) => {
  const { t } = useAppTranslation('kpi');
  const svgId = useId();    // unique ID prefix for SVG defs (clipPaths, etc.)

  // ---- drill-down stack ----
  const [drillStack, setDrillStack] = useState<any[]>([]);
  const currentGroup = drillStack.length
    ? drillStack[drillStack.length - 1]
    : groupPayload;

  // ---- sort mode ----
  const [sortMode, setSortMode] = useState<SortMode>("alpha");

  // ---- aggregation method (synced with KpiGridTable via localStorage) ----
  const [aggMethod, setAggMethod] = useState<AggregationMethod>(readAggMethod);

  const changeAggMethod = useCallback((m: AggregationMethod) => {
    setAggMethod(m);
    writeAggMethod(m);
  }, []);

  // ---- determine if current group has subgroups ----
  const subRows: any[] = currentGroup?.subRows || [];
  const hasSubgroups = subRows.some((r: any) => r?.__type === "group");

  // ---- build items for the bar chart ----
  const isFinance = kpiCategory === "finance";
  const isScoreBased = kpiCategory === "summary" || kpiCategory === "mission" || kpiCategory === "data";

  // Map category to the score/color field prefix used on project rows.
  const scoreFieldMap: Record<string, { scoreKey: string; colorKey: string }> = {
    summary:  { scoreKey: "summary_score_0_100",          colorKey: "summary_indicator_color" },
    mission:  { scoreKey: "mission_score_0_100",          colorKey: "mission_indicator_color" },
    data:     { scoreKey: "data_reliability_score_0_100", colorKey: "data_reliability_color" },
  };

  const items: ProgressItem[] = useMemo(() => {
    const leafItems =
      currentGroup?.group_items ||
      currentGroup?.__items ||
      currentGroup?.items ||
      [];

    if (isScoreBased) {
      const { scoreKey, colorKey } = scoreFieldMap[kpiCategory] || scoreFieldMap.summary;
      return hasSubgroups
        ? buildScoreSubgroupItems(subRows, aggMethod, scoreKey, colorKey)
        : buildScoreProjectItems(leafItems, scoreKey, colorKey);
    }
    if (isFinance) {
      return hasSubgroups
        ? buildFinanceSubgroupItems(subRows, aggMethod)
        : buildFinanceProjectItems(leafItems);
    }
    // Default: schedule
    return hasSubgroups
      ? buildSubgroupItems(subRows, aggMethod)
      : buildProjectItems(leafItems);
  }, [currentGroup, hasSubgroups, subRows, aggMethod, kpiCategory, isFinance, isScoreBased]);

  // ---- apply sort ----
  const sortedItems = useMemo(
    () => applySortMode(items, sortMode),
    [items, sortMode],
  );

  // ---- summary statistics ----
  const stats = useMemo(() => {
    if (!items.length) return null;

    const pcts = items.map((i) => i.percentage);
    const avgPct = pcts.reduce((a, b) => a + b, 0) / pcts.length;

    // Variance from baseline (only items that have baseline)
    const variances = items
      .filter((i) => i.baselinePercentage !== null)
      .map((i) => i.percentage - i.baselinePercentage!);
    const avgVariance = variances.length
      ? variances.reduce((a, b) => a + b, 0) / variances.length
      : null;

    // Average baseline (for thermometer marker)
    const baselinePcts = items
      .filter((i) => i.baselinePercentage !== null)
      .map((i) => i.baselinePercentage!);
    const avgBaseline = baselinePcts.length
      ? baselinePcts.reduce((a, b) => a + b, 0) / baselinePcts.length
      : null;

    // Standard deviation of % complete
    const sqDiffs = pcts.map((p) => (p - avgPct) ** 2);
    const stdDev = Math.sqrt(sqDiffs.reduce((a, b) => a + b, 0) / pcts.length);

    // Color breakdown for donut
    const green = items.filter((i) => i.colorCode === 3).length;
    const yellow = items.filter((i) => i.colorCode === 2).length;
    const red = items.filter((i) => i.colorCode === 1).length;
    const grey = items.filter((i) => i.colorCode === 0 || (i.colorCode !== 1 && i.colorCode !== 2 && i.colorCode !== 3)).length;
    const onTrack = green;

    // Min/max for distribution strip
    const minPct = Math.min(...pcts);
    const maxPct = Math.max(...pcts);

    return { avgPct, avgVariance, avgBaseline, stdDev, onTrack, total: items.length, green, yellow, red, grey, minPct, maxPct };
  }, [items]);

  // ---- category-aware spotlight configuration ----
  type SpotlightConfig = {
    varianceBadge1: string;   // best variance (or best for score)
    varianceBadge2: string;   // worst variance (or worst for score)
    absoluteBadge1: string;   // highest % / spend / score
    absoluteBadge2: string;   // lowest % / spend / score
    showAbsoluteRow: boolean; // score-based only shows 2 cards, not 4
    flipVariance: boolean;    // finance: positive variance = over budget = BAD
  };

  const spotlightCfg: Record<string, SpotlightConfig> = {
    schedule: { varianceBadge1: "Most Ahead",       varianceBadge2: "Most Behind",      absoluteBadge1: "Most Complete",  absoluteBadge2: "Least Complete",  showAbsoluteRow: true,  flipVariance: false },
    finance:  { varianceBadge1: "Most Under Budget", varianceBadge2: "Most Over Budget", absoluteBadge1: "Lowest Spend",   absoluteBadge2: "Highest Spend",  showAbsoluteRow: true,  flipVariance: true },
    summary:  { varianceBadge1: "Highest Score",     varianceBadge2: "Lowest Score",     absoluteBadge1: "",               absoluteBadge2: "",                showAbsoluteRow: false, flipVariance: false },
    mission:  { varianceBadge1: "Highest Score",     varianceBadge2: "Lowest Score",     absoluteBadge1: "",               absoluteBadge2: "",                showAbsoluteRow: false, flipVariance: false },
    data:     { varianceBadge1: "Highest Score",     varianceBadge2: "Lowest Score",     absoluteBadge1: "",               absoluteBadge2: "",                showAbsoluteRow: false, flipVariance: false },
  };
  const cfg = spotlightCfg[kpiCategory] || spotlightCfg.schedule;

  // ---- best performer & biggest risk ----
  const spotlights = useMemo(() => {
    if (items.length < 2) return null;

    const withVariance = items.filter((i) => i.baselinePercentage !== null);

    let best: ProgressItem;
    let worst: ProgressItem;

    if (isScoreBased) {
      // Score-based: sort by raw percentage (score), highest=best, lowest=worst
      const sorted = [...items].sort((a, b) => a.percentage - b.percentage);
      worst = sorted[0];
      best = sorted[sorted.length - 1];
    } else if (withVariance.length >= 2) {
      // Schedule/Finance: sort by variance from baseline
      const sorted = [...withVariance].sort(
        (a, b) =>
          (a.percentage - a.baselinePercentage!) -
          (b.percentage - b.baselinePercentage!),
      );
      if (cfg.flipVariance) {
        // Finance: lowest variance = most under budget = best
        best = sorted[0];
        worst = sorted[sorted.length - 1];
      } else {
        worst = sorted[0];
        best = sorted[sorted.length - 1];
      }
    } else {
      const sorted = [...items].sort((a, b) => a.percentage - b.percentage);
      if (cfg.flipVariance) {
        best = sorted[0];
        worst = sorted[sorted.length - 1];
      } else {
        worst = sorted[0];
        best = sorted[sorted.length - 1];
      }
    }

    const bestVar =
      best.baselinePercentage !== null
        ? best.percentage - best.baselinePercentage
        : null;
    const worstVar =
      worst.baselinePercentage !== null
        ? worst.percentage - worst.baselinePercentage
        : null;

    // Absolute progress spotlights (most / least by raw %)
    const sortedByPct = [...items].sort((a, b) => a.percentage - b.percentage);
    const leastComplete = sortedByPct[0];
    const mostComplete = sortedByPct[sortedByPct.length - 1];

    const mostVar =
      mostComplete.baselinePercentage !== null
        ? mostComplete.percentage - mostComplete.baselinePercentage
        : null;
    const leastVar =
      leastComplete.baselinePercentage !== null
        ? leastComplete.percentage - leastComplete.baselinePercentage
        : null;

    return { best, bestVar, worst, worstVar, mostComplete, mostVar, leastComplete, leastVar };
  }, [items, cfg.flipVariance, isScoreBased]);

  // ---- handlers ----
  const handleBack = useCallback(() => {
    if (drillStack.length > 1) {
      // Pop one level
      setDrillStack((s) => s.slice(0, -1));
    } else if (drillStack.length === 1) {
      // Return to root group
      setDrillStack([]);
    } else {
      // Exit group view entirely
      onBack();
    }
  }, [drillStack.length, onBack]);

  // Tab index for project selection: 0=Summary, 1=Schedule, 2=Finance, 3=Mission, 4=Data
  const categoryTabIndexMap: Record<string, number> = {
    summary: 0, schedule: 1, finance: 2, mission: 3, data: 4,
  };
  const categoryTabIndex = categoryTabIndexMap[kpiCategory] ?? 1;

  const handleItemClick = useCallback(
    (item: ProgressItem) => {
      if (item.isSubgroup) {
        // Drill down into subgroup
        setDrillStack((s) => [...s, item.payload]);
      } else {
        // Select the project — navigate to the matching KPI tab
        onSelectProject(item.payload, categoryTabIndex);
      }
    },
    [onSelectProject, categoryTabIndex],
  );

  // ---- derived display values ----
  const groupLabel =
    currentGroup?.groupLabel ||
    currentGroup?.project?.project_name ||
    "Group";
  const totalCount = sortedItems.length;

  // ---- category-aware labels ----
  const lblMap: Record<string, { complete: string; baseline: string; variance: string; avgComplete: string; onTrack: string }> = {
    schedule: { complete: "Complete",     baseline: "Baseline", variance: "Variance", avgComplete: "Avg Complete", onTrack: "On Track" },
    finance:  { complete: "Budget Spent", baseline: "Budget",   variance: "Variance", avgComplete: "Avg Spent",    onTrack: "On Budget" },
    summary:  { complete: "Score",        baseline: "Baseline", variance: "Variance", avgComplete: "Avg Score",    onTrack: "On Track" },
    mission:  { complete: "Score",        baseline: "Baseline", variance: "Variance", avgComplete: "Avg Score",    onTrack: "On Track" },
    data:     { complete: "Score",        baseline: "Baseline", variance: "Variance", avgComplete: "Avg Score",    onTrack: "On Track" },
  };
  const lbl = lblMap[kpiCategory] || lblMap.schedule;

  // ---- render ----
  return (
    <div className={styles.wrapper}>
      {/* ─── Header ─── */}
      <div className={styles.header}>
        <button
          className={styles.backBtn}
          onClick={handleBack}
          title={t('groupProgress.back')}
          aria-label={t('groupProgress.back')}
        >
          ◄
        </button>
        <div className={styles.headerTitle}>
          {kpiCategoryLabel} Comparison — {groupLabel}
          <span className={styles.headerCount}>
            {" "}
            ({totalCount} {hasSubgroups ? "groups" : "projects"})
          </span>
        </div>
      </div>

      {/* ─── Aggregation method selector (groups of groups only) ─── */}
      {hasSubgroups && (
        <div className={styles.aggRow}>
          <span className={styles.aggLabel}>{t('groupProgress.rollup')}</span>
          {(["average", "best", "worst"] as AggregationMethod[]).map((m) => (
            <button
              key={m}
              className={`${styles.aggBtn} ${aggMethod === m ? styles.aggBtnActive : ""}`}
              onClick={() => changeAggMethod(m)}
            >
              {m.charAt(0).toUpperCase() + m.slice(1)}
            </button>
          ))}
        </div>
      )}

      {/* ─── Bar chart + list (scrollable) ─── */}
      <div className={styles.scrollArea}>
        {sortedItems.length === 0 ? (
          <div className={styles.emptyState}>
            No projects with {kpiCategoryLabel?.toLowerCase() || kpiCategory} data in this group.
          </div>
        ) : (
          <>
            {/* ─── Best / Worst spotlight ─── */}
            {spotlights && (
              <div className={styles.spotlightRow}>
                {/* Best performer */}
                {(() => {
                  const c = spotlights.best.cssColor;
                  const needsDarkText = c === "#ffd13a" || c === "#9CA3AF";
                  return (
                    <button
                      className={styles.spotlightCard}
                      onClick={() => handleItemClick(spotlights.best)}
                      title={`Open ${spotlights.best.label}`}
                    >
                      <div className={styles.spotlightImageWrap}>
                        {spotlights.best.imageUrl ? (
                          <>
                            <img src={mediaSrc(spotlights.best.imageUrl) ?? undefined} alt="" aria-hidden="true" className={styles.spotlightImageBg} />
                            <img src={mediaSrc(spotlights.best.imageUrl) ?? undefined} alt={spotlights.best.label} className={styles.spotlightImage} />
                          </>
                        ) : (
                          <div className={styles.spotlightPlaceholder} style={{ background: `${c}1F` }}>
                            <FitText text={spotlights.best.label} className={styles.spotlightPlaceholderName} />
                          </div>
                        )}
                        <span className={styles.spotlightBadge} style={{ background: c, color: needsDarkText ? "#1e293b" : "#fff" }}>
                          {cfg.varianceBadge1}
                        </span>
                      </div>
                      <div className={styles.spotlightBody}>
                        <div className={styles.spotlightName}>{spotlights.best.label}</div>
                        <div className={styles.spotlightMetrics}>
                          <span className={styles.spotlightMetric}>
                            <span className={styles.spotlightMetricVal} style={{ color: c }}>{formatPercent(spotlights.best.percentage, 1)}</span>
                            <span className={styles.spotlightMetricLbl}>{lbl.complete}</span>
                          </span>
                          {spotlights.bestVar !== null && (
                            <span className={styles.spotlightMetric}>
                              <span className={styles.spotlightMetricVal} style={{ color: c }}>
                                {formatDelta(spotlights.bestVar, 1)}
                              </span>
                              <span className={styles.spotlightMetricLbl}>{lbl.variance}</span>
                            </span>
                          )}
                          {spotlights.best.baselinePercentage !== null && (
                            <span className={styles.spotlightMetric}>
                              <span className={styles.spotlightMetricVal} style={{ color: c }}>{formatPercent(spotlights.best.baselinePercentage, 1)}</span>
                              <span className={styles.spotlightMetricLbl}>{lbl.baseline}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })()}

                {/* Biggest risk */}
                {(() => {
                  const c = spotlights.worst.cssColor;
                  const needsDarkText = c === "#ffd13a" || c === "#9CA3AF";
                  return (
                    <button
                      className={styles.spotlightCard}
                      onClick={() => handleItemClick(spotlights.worst)}
                      title={`Open ${spotlights.worst.label}`}
                    >
                      <div className={styles.spotlightImageWrap}>
                        {spotlights.worst.imageUrl ? (
                          <>
                            <img src={mediaSrc(spotlights.worst.imageUrl) ?? undefined} alt="" aria-hidden="true" className={styles.spotlightImageBg} />
                            <img src={mediaSrc(spotlights.worst.imageUrl) ?? undefined} alt={spotlights.worst.label} className={styles.spotlightImage} />
                          </>
                        ) : (
                          <div className={styles.spotlightPlaceholder} style={{ background: `${c}1F` }}>
                            <FitText text={spotlights.worst.label} className={styles.spotlightPlaceholderName} />
                          </div>
                        )}
                        <span className={styles.spotlightBadge} style={{ background: c, color: needsDarkText ? "#1e293b" : "#fff" }}>
                          {cfg.varianceBadge2}
                        </span>
                      </div>
                      <div className={styles.spotlightBody}>
                        <div className={styles.spotlightName}>{spotlights.worst.label}</div>
                        <div className={styles.spotlightMetrics}>
                          <span className={styles.spotlightMetric}>
                            <span className={styles.spotlightMetricVal} style={{ color: c }}>{formatPercent(spotlights.worst.percentage, 1)}</span>
                            <span className={styles.spotlightMetricLbl}>{lbl.complete}</span>
                          </span>
                          {spotlights.worstVar !== null && (
                            <span className={styles.spotlightMetric}>
                              <span className={styles.spotlightMetricVal} style={{ color: c }}>
                                {formatDelta(spotlights.worstVar, 1)}
                              </span>
                              <span className={styles.spotlightMetricLbl}>{lbl.variance}</span>
                            </span>
                          )}
                          {spotlights.worst.baselinePercentage !== null && (
                            <span className={styles.spotlightMetric}>
                              <span className={styles.spotlightMetricVal} style={{ color: c }}>{formatPercent(spotlights.worst.baselinePercentage, 1)}</span>
                              <span className={styles.spotlightMetricLbl}>{lbl.baseline}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })()}
              </div>
            )}

            {/* ─── Most / Least Complete spotlight (hidden for score-based) ─── */}
            {spotlights && cfg.showAbsoluteRow && (
              <div className={styles.spotlightRow}>
                {/* Most Complete (left) — e.g. "Lowest Spend" for finance */}
                {(() => {
                  const c = spotlights.mostComplete.cssColor;
                  const needsDarkText = c === "#ffd13a" || c === "#9CA3AF";
                  return (
                    <button
                      className={styles.spotlightCard}
                      onClick={() => handleItemClick(spotlights.mostComplete)}
                      title={`Open ${spotlights.mostComplete.label}`}
                    >
                      <div className={styles.spotlightImageWrap}>
                        {spotlights.mostComplete.imageUrl ? (
                          <>
                            <img src={mediaSrc(spotlights.mostComplete.imageUrl) ?? undefined} alt="" aria-hidden="true" className={styles.spotlightImageBg} />
                            <img src={mediaSrc(spotlights.mostComplete.imageUrl) ?? undefined} alt={spotlights.mostComplete.label} className={styles.spotlightImage} />
                          </>
                        ) : (
                          <div className={styles.spotlightPlaceholder} style={{ background: `${c}1F` }}>
                            <FitText text={spotlights.mostComplete.label} className={styles.spotlightPlaceholderName} />
                          </div>
                        )}
                        <span className={styles.spotlightBadge} style={{ background: c, color: needsDarkText ? "#1e293b" : "#fff" }}>
                          {cfg.absoluteBadge1}
                        </span>
                      </div>
                      <div className={styles.spotlightBody}>
                        <div className={styles.spotlightName}>{spotlights.mostComplete.label}</div>
                        <div className={styles.spotlightMetrics}>
                          <span className={styles.spotlightMetric}>
                            <span className={styles.spotlightMetricVal} style={{ color: c }}>{formatPercent(spotlights.mostComplete.percentage, 1)}</span>
                            <span className={styles.spotlightMetricLbl}>{lbl.complete}</span>
                          </span>
                          {spotlights.mostVar !== null && (
                            <span className={styles.spotlightMetric}>
                              <span className={styles.spotlightMetricVal} style={{ color: c }}>
                                {formatDelta(spotlights.mostVar, 1)}
                              </span>
                              <span className={styles.spotlightMetricLbl}>{lbl.variance}</span>
                            </span>
                          )}
                          {spotlights.mostComplete.baselinePercentage !== null && (
                            <span className={styles.spotlightMetric}>
                              <span className={styles.spotlightMetricVal} style={{ color: c }}>{formatPercent(spotlights.mostComplete.baselinePercentage, 1)}</span>
                              <span className={styles.spotlightMetricLbl}>{lbl.baseline}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })()}

                {/* Least Complete (right) — e.g. "Highest Spend" for finance */}
                {(() => {
                  const c = spotlights.leastComplete.cssColor;
                  const needsDarkText = c === "#ffd13a" || c === "#9CA3AF";
                  return (
                    <button
                      className={styles.spotlightCard}
                      onClick={() => handleItemClick(spotlights.leastComplete)}
                      title={`Open ${spotlights.leastComplete.label}`}
                    >
                      <div className={styles.spotlightImageWrap}>
                        {spotlights.leastComplete.imageUrl ? (
                          <>
                            <img src={mediaSrc(spotlights.leastComplete.imageUrl) ?? undefined} alt="" aria-hidden="true" className={styles.spotlightImageBg} />
                            <img src={mediaSrc(spotlights.leastComplete.imageUrl) ?? undefined} alt={spotlights.leastComplete.label} className={styles.spotlightImage} />
                          </>
                        ) : (
                          <div className={styles.spotlightPlaceholder} style={{ background: `${c}1F` }}>
                            <FitText text={spotlights.leastComplete.label} className={styles.spotlightPlaceholderName} />
                          </div>
                        )}
                        <span className={styles.spotlightBadge} style={{ background: c, color: needsDarkText ? "#1e293b" : "#fff" }}>
                          {cfg.absoluteBadge2}
                        </span>
                      </div>
                      <div className={styles.spotlightBody}>
                        <div className={styles.spotlightName}>{spotlights.leastComplete.label}</div>
                        <div className={styles.spotlightMetrics}>
                          <span className={styles.spotlightMetric}>
                            <span className={styles.spotlightMetricVal} style={{ color: c }}>{formatPercent(spotlights.leastComplete.percentage, 1)}</span>
                            <span className={styles.spotlightMetricLbl}>{lbl.complete}</span>
                          </span>
                          {spotlights.leastVar !== null && (
                            <span className={styles.spotlightMetric}>
                              <span className={styles.spotlightMetricVal} style={{ color: c }}>
                                {formatDelta(spotlights.leastVar, 1)}
                              </span>
                              <span className={styles.spotlightMetricLbl}>{lbl.variance}</span>
                            </span>
                          )}
                          {spotlights.leastComplete.baselinePercentage !== null && (
                            <span className={styles.spotlightMetric}>
                              <span className={styles.spotlightMetricVal} style={{ color: c }}>{formatPercent(spotlights.leastComplete.baselinePercentage, 1)}</span>
                              <span className={styles.spotlightMetricLbl}>{lbl.baseline}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })()}
              </div>
            )}

            {/* ─── Visual statistics ─── */}
            {stats && (
              <div className={styles.vizStatsRow}>
                {/* ── 1. Group Average — stats left, thermometer bar right ── */}
                <div className={styles.vizCard} style={{ minWidth: 210 }}>
                  <span className={styles.vizCardTitle}>{t('groupProgress.groupAverage')}</span>
                  {(() => {
                    const pct = Math.max(0, Math.min(100, stats.avgPct));
                    const blPct = stats.avgBaseline !== null ? Math.max(0, Math.min(100, stats.avgBaseline)) : null;
                    const variance = stats.avgVariance;

                    const fillColor =
                      stats.onTrack >= stats.total * 0.6 ? "#45BB47"
                      : stats.onTrack >= stats.total * 0.3 ? "#ffd13a"
                      : stats.total === 0 ? "#9CA3AF"
                      : "#D53541";

                    const statusLabel =
                      kpiCategory === "finance" ? (variance !== null && variance <= 0 ? "ON BUDGET" : "OVER BUDGET")
                      : isScoreBased ? (stats.onTrack >= stats.total * 0.5 ? "HEALTHY" : "AT RISK")
                      : (variance !== null && variance >= 0 ? "ON TRACK" : "BEHIND");
                    const statusColor =
                      statusLabel === "ON TRACK" || statusLabel === "ON BUDGET" || statusLabel === "HEALTHY"
                        ? "#45BB47" : "#D53541";

                    const arrow = variance !== null ? (variance >= 0 ? "▲" : "▼") : "";

                    const barW = 44;
                    const barH = 999; // placeholder — bar stretches to fill via CSS flex
                    const capR = 0;

                    return (
                      <div style={{ display: "flex", gap: 14, width: "100%", flex: 1, alignItems: "center" }}>
                        {/* Left column — value, badge, stats */}
                        <div style={{ flex: "1 1 0", minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 6 }}>
                          {/* Large percentage + arrow */}
                          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                            <span style={{ fontSize: 36, fontWeight: 700, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>
                              {formatPercent(pct, 1)}
                            </span>
                            {arrow && (
                              <span style={{ fontSize: 18, fontWeight: 700, color: fillColor }}>{arrow}</span>
                            )}
                          </div>
                          {/* Category label */}
                          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                            {isScoreBased ? "Score" : kpiCategory === "finance" ? "% Spent" : "% Complete"}
                          </span>
                          {/* Status badge */}
                          <span style={{
                            fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
                            color: "var(--text-primary)", background: statusColor, padding: "3px 10px", borderRadius: 4, lineHeight: 1.4,
                            alignSelf: "flex-start",
                          }}>
                            {statusLabel}
                          </span>

                          {/* Stat rows */}
                          <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 6 }}>
                            {blPct !== null && stats.avgBaseline !== null && (
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, lineHeight: 1.3 }}>
                                <span style={{ color: "var(--text-tertiary)", fontWeight: 500 }}>{lbl.baseline}</span>
                                <span style={{ color: "var(--text-primary)", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                                  {formatPercent(stats.avgBaseline, 1)}
                                </span>
                              </div>
                            )}
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, lineHeight: 1.3 }}>
                              <span style={{ color: "var(--text-tertiary)", fontWeight: 500 }}>{t('groupProgress.average')}</span>
                              <span style={{ color: "var(--text-primary)", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                                {formatPercent(stats.avgPct, 1)}
                              </span>
                            </div>
                            {variance !== null && (
                              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, lineHeight: 1.3 }}>
                                <span style={{ color: "var(--text-tertiary)", fontWeight: 500 }}>Δ Variance</span>
                                <span style={{
                                  fontWeight: 700, fontVariantNumeric: "tabular-nums",
                                  color: (isFinance ? variance <= 0 : variance >= 0) ? "#45BB47" : variance > -10 ? "#ffd13a" : "#D53541",
                                }}>
                                  {formatDelta(variance, 1)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Right column — vertical thermometer bar (stretches to full card height) */}
                        <div style={{ width: barW + 10, minHeight: 120, display: "flex", alignItems: "stretch" }}>
                          <svg width="100%" height="100%" viewBox={`0 0 ${barW + 10} 100`} preserveAspectRatio="none" style={{ display: "block" }}>
                            <defs>
                              <clipPath id={`${svgId}-thermo`}>
                                <rect x={5} y={0} width={barW} height={100} rx={capR} ry={capR} />
                              </clipPath>
                            </defs>
                            <rect x={5} y={0} width={barW} height={100} rx={capR} ry={capR}
                              fill="var(--bg-hover)" />
                            <rect x={5} y={100 - pct} width={barW} height={pct}
                              fill={fillColor} opacity={0.85} clipPath={`url(#${svgId}-thermo)`} />
                            {blPct !== null && (
                              <line x1={2} y1={100 - blPct} x2={5 + barW + 3} y2={100 - blPct}
                                stroke="var(--text-primary)" strokeWidth={2.5} strokeLinecap="round" opacity={0.9} />
                            )}
                          </svg>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* ── 2. Distribution Strip (Spread) — scales to fill card ── */}
                <div className={styles.vizCard} style={{ flex: 1.3 }}>
                  <span className={styles.vizCardTitle}>{t('groupProgress.distribution')}</span>
                  {(() => {
                    // SVG viewBox coordinates — the actual rendered size stretches via width="100%"
                    const W = 300;
                    const H = 150;
                    const stripTop = 16;
                    const stripH = 80;
                    const stripLeft = 20;
                    const stripRight = W - 20;
                    const stripW = stripRight - stripLeft;

                    const buckets = new Map<number, ProgressItem[]>();
                    for (const item of items) {
                      const bucketKey = Math.round(item.percentage * 2);
                      if (!buckets.has(bucketKey)) buckets.set(bucketKey, []);
                      buckets.get(bucketKey)!.push(item);
                    }

                    const dotR = Math.max(5, Math.min(9, 60 / Math.sqrt(items.length)));
                    const avgX = stripLeft + (Math.max(0, Math.min(100, stats.avgPct)) / 100) * stripW;

                    return (
                      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                        <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
                          {/* Track background */}
                          <rect x={stripLeft} y={stripTop + stripH / 2 - 5} width={stripW} height={10}
                            rx={5} fill="var(--bg-hover)" />

                          {/* Project dots */}
                          {Array.from(buckets.entries()).map(([bk, group]) =>
                            group.map((item, gi) => {
                              const x = stripLeft + (Math.max(0, Math.min(100, item.percentage)) / 100) * stripW;
                              const y = stripTop + stripH / 2 - gi * (dotR * 2.2);
                              return (
                                <circle key={item.id} cx={x} cy={y} r={dotR}
                                  fill={item.cssColor} opacity={0.85}
                                  stroke="rgba(0,0,0,0.3)" strokeWidth={0.5} />
                              );
                            })
                          )}

                          {/* Average line */}
                          <line x1={avgX} y1={stripTop - 2} x2={avgX} y2={stripTop + stripH + 2}
                            stroke="var(--text-primary)" strokeWidth={2.5} strokeDasharray="5 3" opacity={0.7} />

                          {/* Scale labels */}
                          <text x={stripLeft} y={stripTop + stripH + 22} textAnchor="start"
                            fill="var(--text-muted)" fontSize="16" fontWeight="500" fontFamily="inherit">0%</text>
                          <text x={stripRight} y={stripTop + stripH + 22} textAnchor="end"
                            fill="var(--text-muted)" fontSize="16" fontWeight="500" fontFamily="inherit">100%</text>
                          <text x={avgX} y={stripTop + stripH + 22} textAnchor="middle"
                            fill="var(--text-tertiary)" fontSize="16" fontWeight="600" fontFamily="inherit">
                            avg
                          </text>

                          {/* Range + std dev */}
                          <text x={W / 2} y={H - 4} textAnchor="middle"
                            fill="var(--text-tertiary)" fontSize="15" fontFamily="inherit">
                            {formatPercent(stats.minPct, 0)} – {formatPercent(stats.maxPct, 0)}  · σ {formatFixed(stats.stdDev, 1)}
                          </text>
                        </svg>
                      </div>
                    );
                  })()}
                </div>

                {/* ── 3. Health Donut (On-Track Ratio) — scales to fill card ── */}
                <div className={styles.vizCard}>
                  <span className={styles.vizCardTitle}>{lbl.onTrack}</span>
                  {(() => {
                    const size = 102;
                    const cx = size / 2;
                    const cy = size / 2;
                    const outerR = 41;
                    const innerR = 28;

                    const segments = [
                      { count: stats.green,  color: "#45BB47", label: "Green" },
                      { count: stats.yellow, color: "#ffd13a", label: "Yellow" },
                      { count: stats.red,    color: "#D53541", label: "Red" },
                      { count: stats.grey,   color: "#4B5563", label: "No data" },
                    ].filter((s) => s.count > 0);

                    const total = stats.total || 1;

                    let cumAngle = -Math.PI / 2;
                    const gap = segments.length > 1 ? 0.03 : 0;

                    const arcs = segments.map((seg) => {
                      const frac = seg.count / total;
                      const startAngle = cumAngle + gap / 2;
                      // Cap sweep just under 2π — a full-circle SVG arc (start == end) renders as nothing
                      const rawSweep = frac * Math.PI * 2 - gap;
                      const sweep = Math.min(rawSweep, Math.PI * 2 - 0.001);
                      cumAngle += frac * Math.PI * 2;

                      if (sweep <= 0) return null;

                      const x1o = cx + outerR * Math.cos(startAngle);
                      const y1o = cy + outerR * Math.sin(startAngle);
                      const x2o = cx + outerR * Math.cos(startAngle + sweep);
                      const y2o = cy + outerR * Math.sin(startAngle + sweep);
                      const x1i = cx + innerR * Math.cos(startAngle + sweep);
                      const y1i = cy + innerR * Math.sin(startAngle + sweep);
                      const x2i = cx + innerR * Math.cos(startAngle);
                      const y2i = cy + innerR * Math.sin(startAngle);

                      const largeArc = sweep > Math.PI ? 1 : 0;

                      const d = [
                        `M ${x1o} ${y1o}`,
                        `A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2o} ${y2o}`,
                        `L ${x1i} ${y1i}`,
                        `A ${innerR} ${innerR} 0 ${largeArc} 0 ${x2i} ${y2i}`,
                        "Z",
                      ].join(" ");

                      return { d, color: seg.color, label: seg.label, count: seg.count };
                    }).filter(Boolean) as { d: string; color: string; label: string; count: number }[];

                    return (
                      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width="100%" viewBox={`0 0 ${size} ${size}`} preserveAspectRatio="xMidYMid meet" style={{ display: "block" }}>
                          {/* Background ring */}
                          <circle cx={cx} cy={cy} r={(outerR + innerR) / 2}
                            fill="none" stroke="var(--bg-hover)"
                            strokeWidth={outerR - innerR} />
                          {/* Segments */}
                          {arcs.map((arc, i) => (
                            <path key={i} d={arc.d} fill={arc.color} opacity={0.85}>
                              <title>{arc.label}: {arc.count}</title>
                            </path>
                          ))}
                          {/* Center text */}
                          <text x={cx} y={cy - 2} textAnchor="middle"
                            fill="var(--text-primary)" fontSize="22" fontWeight="700" fontFamily="inherit">
                            {stats.green}
                          </text>
                          <text x={cx} y={cy + 12} textAnchor="middle"
                            fill="var(--text-tertiary)" fontSize="11" fontWeight="500" fontFamily="inherit">
                            of {stats.total}
                          </text>
                        </svg>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* ─── Sort selector ─── */}
            {items.length > 1 && (
              <div className={styles.aggRow}>
                <span className={styles.aggLabel}>{t('groupProgress.sort')}</span>
                {SORT_OPTIONS.map((opt) => (
                  <button
                    key={opt.key}
                    className={`${styles.aggBtn} ${sortMode === opt.key ? styles.aggBtnActive : ""}`}
                    onClick={() => setSortMode(opt.key)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* ─── Horizontal bar chart ─── */}
            <div className={styles.chartSection}>
              {sortedItems.map((item) => (
                <button
                  key={item.id}
                  className={styles.barRow}
                  onClick={() => handleItemClick(item)}
                  title={`${item.label} — ${formatPercent(item.percentage, 1)}`}
                >
                  <span className={styles.barLabel}>{item.label}</span>
                  <div className={styles.barTrack}>
                    <div
                      className={styles.barFill}
                      style={{
                        width: `${Math.max(0, Math.min(100, item.percentage))}%`,
                        backgroundColor: item.cssColor,
                      }}
                    />
                    {item.criticalPct !== null && (
                      <div
                        className={styles.criticalTick}
                        style={{ left: `${item.criticalPct}%` }}
                        title={`Critical: ${formatPercent(item.criticalPct, 1)}`}
                      />
                    )}
                    {item.warningPct !== null && (
                      <div
                        className={styles.warningTick}
                        style={{ left: `${item.warningPct}%` }}
                        title={`Warning: ${formatPercent(item.warningPct, 1)}`}
                      />
                    )}
                    {item.baselinePercentage !== null && (
                      <div
                        className={styles.baselineTick}
                        style={{ left: `${item.baselinePercentage}%` }}
                        title={`Baseline: ${formatPercent(item.baselinePercentage, 1)}`}
                      />
                    )}
                  </div>
                  <span className={styles.barPct}>
                    {formatPercent(item.percentage, 1)}
                  </span>
                </button>
              ))}
            </div>

          </>
        )}
      </div>
    </div>
  );
};

export default GroupProgressPage;
